import { IconName, useCallbackRef, type CareOrder } from '@talos/kyoko';
import type { IRowNode, MenuItemDef } from 'ag-grid-community';
import { isCareOrderRow, type CareOrderBlotterEntity } from '../types';
import {
  getCareOrderBlotterChildRows,
  validateManualFillCareOrders,
  type ManualFillCareOrderSelection,
} from './manualFillCareOrdersEligibility';

interface GetManualFillCareOrdersMenuItemParams {
  selectedNodes: IRowNode<CareOrderBlotterEntity>[];
}

interface UseManualFillCareOrdersMenuItemParams {
  // The dialog is hoisted to CareOrderBlotter because both the primary action column and this context-menu item open it.
  onManualFillCareOrders: (careOrders: CareOrder[]) => void;
}

export const useManualFillCareOrdersMenuItem = ({
  onManualFillCareOrders,
}: UseManualFillCareOrdersMenuItemParams): {
  getMenuItem: (params: GetManualFillCareOrdersMenuItemParams) => MenuItemDef | null;
} => {
  const getMenuItem = useCallbackRef(({ selectedNodes }: GetManualFillCareOrdersMenuItemParams) => {
    const selections = getManualFillCareOrderSelections(selectedNodes);
    if (selections.length === 0) {
      return null;
    }

    const validationResult = validateManualFillCareOrders(selections);
    const selectedCareOrders = selections.map(selection => selection.careOrder);

    return {
      name: `Manually fill ${selectedCareOrders.length} Care Order${selectedCareOrders.length === 1 ? '' : 's'}`,
      action: () => {
        if (!validationResult.manuallyFillable) {
          return;
        }

        onManualFillCareOrders(selectedCareOrders);
      },
      icon: `<i class="ag-icon ${IconName.Cash}"/>`,
      disabled: !validationResult.manuallyFillable,
      tooltip: validationResult.reason,
    } satisfies MenuItemDef;
  });

  return {
    getMenuItem,
  };
};

function getManualFillCareOrderSelections(nodes: IRowNode<CareOrderBlotterEntity>[]): ManualFillCareOrderSelection[] {
  return nodes.filter(isCareOrderNode).map(node => {
    return {
      careOrder: node.data,
      childRows: getCareOrderBlotterChildRows(node),
    };
  });
}

function isCareOrderNode(
  node: IRowNode<CareOrderBlotterEntity>
): node is IRowNode<CareOrderBlotterEntity> & { data: CareOrder } {
  return isCareOrderRow(node.data);
}
